import React, { useEffect, useState } from 'react';
import { calculateAge, getDateFromIso8601String } from './DatePicker';

// Options to use when calling toLocaleDateString
const DATE_DISPLAY_OPTIONS = { month: 'long', day: 'numeric', year: 'numeric' };

/**
 * Display a date picker with individual day, month, year inputs.
 *
 * @param props.value The initial ISO 8601 date string ('yyyy-mm-dd')
 * @param props.onChange The function to call, passing the new date string, when the date changes
 * @param props.required A flag to indicate if the input fields should be marked as required
 * @param props.displayAge A flag to indicate if the calculated Age field should be displayed
 * @param props.displayDateString A flag to indicate if the local date string should be displayed
 * @param props.disabled A flag to indicate if the input fields should be disabled
 * @param props.hideInputLabels A flag to indicate if the "Month", "Day", "Year" labels should be hidden
 */
export default function DateOfBirthInput({ userInfo, value, onChange, required, displayAge, displayDateString, disabled, hideInputLables }) {
	const initialFieldValues = getDateFieldsFromString(value);
	const [day, setDay] = useState(initialFieldValues?.day ?? '');
	const [month, setMonth] = useState(initialFieldValues?.month ?? '');
	const [year, setYear] = useState(initialFieldValues?.year ?? '');
	const [date, setDate] = useState(null);
	const [age, setAge] = useState('');

	// Build a new date string any time a day or month changes and pass the new date value to props.onChange
	useEffect(() => {
		let newDate = buildDateString(year, month, day);
		setDate(newDate);
		setAge(calculateAge(getDateFromIso8601String(newDate)) ?? '');
		propagateDate(newDate);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [day, month]);

	/*
	 * Build a new date string any time a year changes and rebuild the full date and age values.
	 * Pass the new date value to props.onChange if the year is 4 digits.
	 * If we were to pass the date when the year is only 2 digits, the 2-to-4 digit conversion 
	 *  would alter the input field and would not allow the user to type a 4 digit year.
	 */
	useEffect(() => {
		let newDate = buildDateString(year, month, day);
		setDate(newDate);
		setAge(calculateAge(getDateFromIso8601String(newDate)) ?? '');
		if (year?.length === 4) {
			propagateDate(newDate);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [year]);

	/*
	 * Rebuild the inputs any time the passed in value changes any of the form fields.
	 * This takes into account the field conversions. So '1' -> '01' would be considered the same 
	 *  value and would not change any fields.
	 */
	useEffect(() => {
		const newFieldValues = getDateFieldsFromString(value);
		if (newFieldValues && newFieldValues.day && newFieldValues.month && newFieldValues.year) {
			let newDate = buildDateString(newFieldValues.year, newFieldValues.month, newFieldValues.day);
			if (newDate !== date) {
				setDay(newFieldValues?.day ?? '');
				setMonth(newFieldValues?.month ?? '');
				setYear(newFieldValues?.year ?? '');
				
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	/**
	 * Pass the constructed date value to the parent through props.onChange.
	 * If the date is in the future, an empty value will be passed to the parent.
	 */
	const propagateDate = (dateStr) => {
		if (onChange) {
			let newDate = (dateStr && typeof dateStr === 'string') ? dateStr : buildDateString(year, month, day);
			let newDateObj = getDateFromIso8601String(newDate);

			// Clear the date and year values if the given date is in the future
			if (newDateObj > new Date()) {
				newDate = '';
				setYear('');
			}

			onChange(newDate);
		}
	}

	/**
	 * Strip non-numeric characters and limit the value to <= 31
	 */
	const handleDayChange = (event) => {
		let value = event.target.value?.replace(/\D/g, '');
		if (value && +value > 31) {
			value = 31;
		}

		setDay(`${value}`);
	};

	/**
	 * Strip non-numeric characters and limit the value to <= 12
	 */
	const handleMonthChange = (event) => {
		let value = event.target.value?.replace(/\D/g, '');
		if (value && +value > 12) {
			value = 12;
		}
		setMonth(`${value}`);
	};

	/**
	 * Strip non-numeric characters
	 */
	const handleYearChange = (event) => {
		setYear(`${event.target.value?.replace(/\D/g, '')}`);
	};

	/**
	 * Handle pressing 'Enter' while focussed on the year field.
	 * This is needed when 'Enter' is pressed with a 2-digit year. This will push the 4-digit year
	 *  to the parent before submitting the form.
	 */ 
	const handleYearKeyDown = (event) => {
		if (event.key === 'Enter') {
			propagateDate();
		}
	}

	// Construct the String representation of the date if it should be displayed
	let dateDisplay = displayDateString && <br />;
	if (date && displayDateString) {
		dateDisplay = getDateFromIso8601String(date).toLocaleDateString(
			navigator?.language || 'en-US',
			DATE_DISPLAY_OPTIONS
		);
	}

	// Common style for the divs surroding the input fields
	const divStyle = { width: '60px', display: 'inline-block', paddingRight: '6px' };

	// Common style for the input field labels
	const labelStyle = { color: '#959595', fontSize: '.9em' };

	// Common style for the input fields
	const inputStyle = { paddingRight: '10px' };

	// The minimum width of this component to keep all input fields on a single line
	const minWidth = displayAge ? '270px' : '200px';

	const dayField = (
		<div style={divStyle}>
			{!hideInputLables && (
				<label htmlFor="dob-day" style={labelStyle}>
					Day
				</label>
			)}
			<input
				type="text"
				autoComplete={userInfo?.type === 'prc' ? "bday-day" : "new-password"}
				className="form-control"
				id="dob-day"
				placeholder="DD"
				style={inputStyle}
				value={day}
				onChange={handleDayChange}
				min="1"
				max="31"
				required={required}
				disabled={disabled}
			/>
		</div>
	);

	const monthField = (
		<div style={divStyle}>
			{!hideInputLables && (
				<label htmlFor="dob-month" style={labelStyle}>
					Month
				</label>
			)}
			<input
				type="text"
				autoComplete={userInfo?.type === 'prc' ? "bday-month" : "new-password"}
				className="form-control"
				id="dob-month"
				placeholder="MM"
				style={inputStyle}
				value={month}
				onChange={handleMonthChange}
				min="1"
				max="12"
				required={required}
				disabled={disabled}
			/>
		</div>
	);

	// Flip the month and day fields for 'en-US' users
	const language = navigator?.language?.toLowerCase();
	const monthFirst = language === 'en' || language === 'en-us';

	return (
		<>
			<div style={{ minWidth: minWidth }}>
				{monthFirst ? (
					<>
						{monthField}
						{dayField}
					</>
				) : (
					<>
						{dayField}
						{monthField}
					</>
				)}

				<div style={{ ...divStyle, width: '80px' }}>
					{!hideInputLables && (
						<label htmlFor="dob-year" style={labelStyle}>
							Year
						</label>
					)}
					<input
						type="text"
						autoComplete={userInfo?.type === 'prc' ? "bday-year" : "new-password"}
						className="form-control"
						id="dob-year"
						placeholder="YYYY"
						style={inputStyle}
						value={year}
						onChange={handleYearChange}
						onBlur={propagateDate}
						onKeyDown={handleYearKeyDown}
						maxLength="4"
						required={required}
						disabled={disabled}
					/>
				</div>

				{displayAge && (
					<div style={{ display: 'inline-block', paddingLeft: '20px' }}>
						<label className="control-label" htmlFor="patient-age">
							Age
						</label>
						<input
							className="form-control"
							disabled={true}
							value={age}
							style={{ width: '3em', padding: '5px' }}
						/>
					</div>
				)}
			</div>
			{dateDisplay && (
				<span id="dobHelpBlock" className="text-info" style={{ fontSize: '.9em' }}>
					{dateDisplay}
				</span>
			)}
		</>
	);
}

/**
 * Build the ISO 8601 date string from the inputs.
 * This will pad day and month values with leading zeros and will convert 2-digit years into 4 digits.
 */
const buildDateString = (year, month, day) => {
	if (!(day && month && year)) {
		return null;
	}

	let formattedMonth = padLeadingZeros(month);
	let formattedDay = padLeadingZeros(day);
	let formattedYear = formatYear(year);

	if (formattedYear == null || formattedMonth === '00' || formattedDay === '00') {
		return null;
	}

	return `${formattedYear}-${formattedMonth}-${formattedDay}`;
};

/**
 * 4-digit years are returned as-is.
 * 2-digit years are converted to 4-digit by comparing to the current year. Before the current year = 20xx. After the current year = 19xx.
 * Anything else is not a valid year and null is returned.
 */
const formatYear = (year) => {
	if (year?.length === 4) {
		return year;
	} else if (year?.length !== 2) {
		return null;
	}

	const paddedYear = padLeadingZeros(year);
	const currentYear = new Date().getFullYear();
	return paddedYear > currentYear % 100 ? `19${paddedYear}` : `20${paddedYear}`;
};

const padLeadingZeros = (input) => ('00' + input).slice(-2);

/**
 * Parse the given ISO-8601 date string and return an object with the year, month, day values.
 * If anything fails (null string, missing a piece, etc.) then return null.
 */
const getDateFieldsFromString = (dateStr) => {
	if (!dateStr) {
		return null;
	}
	const [year, month, day] = dateStr.split('-');
	if (!(year && month && day)) {
		return null;
	}

	return {
		year: year,
		month: month,
		day: day
	};
};
